// Champion context — the local player's champion state plus the two intents.
// `null` (no provider, or the module is off) means "render no champion UI".

import type { GameSnapshot, LegendKindRow, LegendPlayerState, LegendRows } from '@aod/protocol';
import { type ReactNode, createContext, useContext, useEffect, useMemo } from 'react';
import { abilitiesOf } from './championKinds.js';
import {
  doctrineTier as doctrineTierOf,
  legendRows,
  localLegend,
  offeredKind,
  playerFaith,
  playerGold,
} from './championRules.js';
import { useChampionUi } from './championStore.js';

export interface ChampionApi {
  readonly localPlayerIndex: number;
  /** The latest snapshot (scene nodes for stance targets, unit lookups for casts). */
  readonly snapshot: GameSnapshot | null;
  readonly legend: LegendPlayerState;
  readonly tick: number;
  readonly gold: number;
  readonly faith: number;
  /** Snapshot `legendRows` - the single source of truth for every balance number. */
  readonly rows: LegendRows;
  /** The kind this player is (or would be) offered, with its row. */
  readonly offered: { readonly kind: string; readonly row: LegendKindRow };
  readonly doctrineTier: number;
  /** Null = accepted, else the sim's rejection code. */
  readonly summon: (cityId: string) => string | null;
  readonly activate: (unitId: string, slot: number, q: number, r: number) => string | null;
}

const ChampionCtx = createContext<ChampionApi | null>(null);

export function useChampion(): ChampionApi | null {
  return useContext(ChampionCtx);
}

interface ProviderProps {
  readonly snapshot: GameSnapshot | null;
  readonly tick: bigint;
  readonly localPlayerIndex: number;
  readonly summon: ChampionApi['summon'];
  readonly activate: ChampionApi['activate'];
  readonly children: ReactNode;
}

export function ChampionProvider(p: ProviderProps) {
  const legend = localLegend(p.snapshot, p.localPlayerIndex);
  const rows = legendRows(p.snapshot);
  const offered = legend && rows ? offeredKind(p.snapshot, rows, legend, p.localPlayerIndex) : null;
  const gold = playerGold(p.snapshot, p.localPlayerIndex);
  const faith = playerFaith(p.snapshot, p.localPlayerIndex);
  const doctrineTier = offered ? doctrineTierOf(p.snapshot, p.localPlayerIndex, offered.row) : 0;
  const tick = Number(p.tick);
  const { summon, activate, localPlayerIndex } = p;
  const offeredKey = offered?.kind;
  // The event log needs the local champion's ability keys by slot (the event carries only the slot).
  const abilityKeys = useMemo(
    () => (rows && offeredKey ? abilitiesOf(rows, offeredKey).map((a) => a.key) : []),
    [rows, offeredKey],
  );
  useEffect(() => useChampionUi.getState().setLocalAbilityKeys(abilityKeys), [abilityKeys]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `offered` is derived from (rows, legend, offeredKey)
  const value = useMemo<ChampionApi | null>(
    () =>
      legend && rows && offered
        ? {
            localPlayerIndex,
            snapshot: p.snapshot,
            legend,
            tick,
            gold,
            faith,
            rows,
            offered,
            doctrineTier,
            summon,
            activate,
          }
        : null,
    [
      p.snapshot,
      legend,
      rows,
      offeredKey,
      tick,
      gold,
      faith,
      doctrineTier,
      summon,
      activate,
      localPlayerIndex,
    ],
  );
  return <ChampionCtx.Provider value={value}>{p.children}</ChampionCtx.Provider>;
}
